import { useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { Home, X, Sparkles } from 'lucide-react'
import { useHomescreen } from '@/shared/lib/telegram'
import { cn } from '@/shared/lib/utils'

/**
 * Баннер для добавления приложения на домашний экран
 * 
 * Показывается только если:
 * - Функция поддерживается устройством
 * - Пользователь ещё не добавил приложение
 * - Пользователь не закрыл баннер в этой сессии
 * 
 * Bot API 8.0+
 */
export function HomeScreenBanner() {
  const { canAdd, isSupported, addToHomeScreen, loading } = useHomescreen()
  const [dismissed, setDismissed] = useState(false)
  
  // Не показываем если загрузка, не поддерживается, уже добавлено или закрыто
  if (loading || !isSupported || !canAdd || dismissed) {
    return null
  }
  
  const handleAdd = () => {
    addToHomeScreen()
    setDismissed(true)
  }
  
  const handleDismiss = () => {
    setDismissed(true)
  }
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.95 }}
        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        className={cn(
          'relative overflow-hidden rounded-2xl',
          'bg-gradient-to-r from-primary/10 via-primary/5 to-amber-500/10',
          'dark:from-primary/20 dark:via-primary/10 dark:to-amber-500/20',
          'border border-primary/20 dark:border-primary/30',
          'p-4'
        )}
      >
        {/* Декоративные элементы */}
        <div className="absolute -top-6 -right-6 w-24 h-24 bg-primary/10 rounded-full blur-2xl" />
        <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-amber-500/10 rounded-full blur-xl" />
        
        <div className="relative flex items-start gap-3">
          {/* Иконка */}
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary/20 dark:bg-primary/30 flex items-center justify-center">
            <Home className="w-5 h-5 text-primary dark:text-primary-light" />
          </div>
          
          {/* Контент */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              <h3 className="font-semibold text-ink dark:text-white text-sm">
                Быстрый доступ
              </h3>
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
              Добавьте приложение на домашний экран для мгновенного запуска
            </p>
            
            {/* Кнопка */}
            <button
              type="button"
              onClick={handleAdd}
              className={cn(
                'inline-flex items-center gap-1.5 px-4 py-2 rounded-xl',
                'bg-primary dark:bg-primary-dark text-white',
                'text-xs font-medium',
                'active:scale-95 transition-transform',
                'shadow-sm'
              )}
            >
              <Home className="w-3.5 h-3.5" />
              Добавить
            </button>
          </div>
          
          {/* Кнопка закрытия */}
          <button
            type="button"
            onClick={handleDismiss}
            className="flex-shrink-0 p-1.5 -mr-1 -mt-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
            aria-label="Закрыть"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

